import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { InsuranceService } from '../../modules/insurance/insurance.service';
import { PatientInsuranceListViewDataModel } from '../../modules/insurance/models/patient-insurance-list-view-data-view.model';
import { ConfirmComponent } from '../confirm-dialog/confirm.component';
import { ARService } from '../../modules/ar/ar.service';
import { UiService } from 'src/app/services/ui.service';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'app-insurance-change-dialog',
  templateUrl: './insurance-change-dialog.component.html',
  styleUrls: ['./insurance-change-dialog.component.scss']
})
export class InsuranceChangeDialogComponent implements OnInit {
  isLoading: boolean = false;
  insuranceList: PatientInsuranceListViewDataModel[] = [];
  selectedInsurance: PatientInsuranceListViewDataModel | null = null;

  constructor(
    public readonly dialogRef: MatDialogRef<InsuranceChangeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly insuranceService: InsuranceService,
    private readonly dialog: MatDialog,
    private readonly arService: ARService,
    private readonly changeRef: ChangeDetectorRef,
    private readonly uiService: UiService
  ) { }

  ngOnInit(): void {
    this.loadInsuranceList();
  }

  async loadInsuranceList(): Promise<void> {
    this.isLoading = true;
    try {
      this.insuranceList = await firstValueFrom(this.insuranceService.getPatientInsuranceList(this.data.patientId));
      
      this.selectedInsurance = this.insuranceList?.find(insurance => insurance.PatientInsuranceID === this.data.currentInsuranceId) || null;
      // Filter out expired insurance if needed (optional)
      // this.insuranceList = this.insuranceList.filter(insurance => !insurance.isExpired);
      
    } catch (error) {
      console.error('Error loading insurance list:', error);
      this.createMessage('Error loading insurance list', 'Error');
    } finally {
      this.isLoading = false;
      this.changeRef.detectChanges();
    }
  }

  onInsuranceClick(insurance: PatientInsuranceListViewDataModel): void {
    this.selectedInsurance = insurance;
  }

  getInsuranceOrderText(order: number): string {
    switch (order) {
      case 0: return 'Primary';
      case 1: return 'Secondary';
      case 2: return 'Tertiary';
      default: return `Order ${order + 1}`;
    }
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  onSaveClick(): void {
    if (!this.selectedInsurance) {
      return;
    }
    if (this.selectedInsurance.PatientInsuranceID === this.data.currentInsuranceId) {
      this.onCancelClick();
      return;
    }


    // Show confirmation dialog
    const confirmDialogRef = this.dialog.open(ConfirmComponent, {
      width: '400px',
      data: {
        title: 'Confirm Insurance Change',
        message: 'This operation will update all the items in the Transaction with the new Patient Insurance, are you sure?',
        isShowCancel: false,
        isShowNo: true
      }
    });

    confirmDialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        // User clicked Ok/Yes
        this.performInsuranceChange();
      }
      // If result is false or undefined, just close the confirmation dialog
    });
  }

  private async performInsuranceChange(): Promise<void> {
    if (!this.selectedInsurance || !this.data.arTransactionItemId) {
      this.createMessage('Missing required data for insurance change', 'Error');
      return;
    }

    this.isLoading = true;
    
    try {
      const result = await firstValueFrom(this.arService.updatePatientInsurance(
        this.data.arTransactionItemId,
        this.selectedInsurance.PatientInsuranceID
      ));

      // Success response — set loading false and detect changes before closing
      // to avoid calling detectChanges() on a destroyed view
      this.isLoading = false;
      this.changeRef.detectChanges();
      this.createMessage(result.Message || 'Insurance updated successfully', 'Success');
      this.dialogRef.close({
        selectedInsurance: this.selectedInsurance,
        action: 'save',
        success: true,
        result: result
      });

    } catch (error) {
      console.error('Error updating insurance:', error);

      // Handle error response
      let errorMessage = 'Failed to update insurance';
      if (error?.error?.message) {
        errorMessage = error.error.message;
      } else if (error?.error?.Message) {
        errorMessage = error.error.Message;
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.Message) {
        errorMessage = error.Message;
      }

      this.isLoading = false;
      this.changeRef.detectChanges();
      this.createMessage(errorMessage, 'Error');
    }
  }

  private createMessage(message: string, type: string): void {
    this.uiService.displayMessage(type, message);
  }
}

